import { useState } from 'react';
import {
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { IWorkspacePlanModel } from 'api/models/response/workspacePlan/IWorkspacePlanModel';
import { useUpdateWorkspacePlan } from 'hooks/workspacePlan/useUpdateWorkspacePlan';
import { useCreateWorkspacePlan } from 'hooks/workspacePlan/useCreateWorkspacePlan';
import { useDeleteWorkspacePlan } from 'hooks/workspacePlan/useDeleteWorkspacePlan';
import { handleApiError } from 'helpers/handleApiError';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { WorkspacePlanRow } from './WorkspacePlanRow';
import { WorkspacePlanFormValues, formValuesToRequest } from './workspacePlanForm';
import { AddPlanButton, HeaderTitle, HintText, PlansTableContainer, SectionTitle, TableHeader } from './SubscriptionPlan.styles';

const NEW_ROW_KEY = 'new';

interface IWorkspacePlansTableProps {
    plans: IWorkspacePlanModel[];
    currentPlanId?: string;
    refetching: boolean;
}

export const WorkspacePlansTable = ({ plans, currentPlanId, refetching }: IWorkspacePlansTableProps) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { updatePlan, updatingPlan } = useUpdateWorkspacePlan();
    const { createPlan, creatingPlan } = useCreateWorkspacePlan();
    const { deletePlan, deletingPlan } = useDeleteWorkspacePlan();

    const [savingRowKey, setSavingRowKey] = useState<string | null>(null);
    const [activeRowKey, setActiveRowKey] = useState<string | null>(null);
    const isAddingRow = activeRowKey === NEW_ROW_KEY;
    const canEdit = activeRowKey === null;

    const [planToDelete, setPlanToDelete] = useState<IWorkspacePlanModel | null>(null);
    const [deletingPlanId, setDeletingPlanId] = useState<string | null>(null);

    const handleDelete = async () => {
        if (!planToDelete) {
            return;
        }

        setDeletingPlanId(planToDelete.id);
        try {
            await deletePlan(planToDelete.id);
            enqueueSnackbar(t('subscriptionPlan.deleteSuccess'), { variant: 'success' });
        } catch (error) {
            handleApiError(error, { t, enqueueSnackbar });
        } finally {
            setDeletingPlanId(null);
            setPlanToDelete(null);
        }
    };

    const handleUpdate = async (plan: IWorkspacePlanModel, values: WorkspacePlanFormValues): Promise<boolean> => {
        setSavingRowKey(plan.id);
        try {
            await updatePlan({ planId: plan.id, ...formValuesToRequest(values, plan) });
            setActiveRowKey(null);
            enqueueSnackbar(t('subscriptionPlan.updateSuccess'), { variant: 'success' });
            return true;
        } catch (error) {
            handleApiError(error, { t, enqueueSnackbar });
            return false;
        } finally {
            setSavingRowKey(null);
        }
    };

    const handleCreate = async (values: WorkspacePlanFormValues): Promise<boolean> => {
        setSavingRowKey(NEW_ROW_KEY);
        try {
            await createPlan(formValuesToRequest(values));
            setActiveRowKey(null);
            enqueueSnackbar(t('subscriptionPlan.createSuccess'), { variant: 'success' });
            return true;
        } catch (error) {
            handleApiError(error, { t, enqueueSnackbar });
            return false;
        } finally {
            setSavingRowKey(null);
        }
    };

    const hasRows = plans.length > 0 || isAddingRow;

    return (
        <div>
            <TableHeader>
                <HeaderTitle>
                    <SectionTitle variant="h6">{t('subscriptionPlan.plansTitle')}</SectionTitle>
                    {refetching && <CircularProgress size={16} />}
                </HeaderTitle>
                <AddPlanButton
                    variant="outlined"
                    color="secondary"
                    startIcon={<AddIcon />}
                    disabled={!canEdit}
                    onClick={() => setActiveRowKey(NEW_ROW_KEY)}
                    data-testid="create-plan-button"
                >
                    {t('subscriptionPlan.addPlan')}
                </AddPlanButton>
            </TableHeader>

            <HintText variant="body2">
                {t('subscriptionPlan.unlimitedHint')}
            </HintText>

            {!hasRows ? (
                <Typography color="textSecondary">{t('subscriptionPlan.empty')}</Typography>
            ) : (
                <PlansTableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('subscriptionPlan.name')} *</TableCell>
                                <TableCell>{t('subscriptionPlan.code')}</TableCell>
                                <TableCell>{t('subscriptionPlan.storageLimitGb')} *</TableCell>
                                <TableCell>{t('subscriptionPlan.maxUsers')} *</TableCell>
                                <TableCell>{t('subscriptionPlan.maxGogglesCount')} *</TableCell>
                                <TableCell>{t('subscriptionPlan.type')}</TableCell>
                                <TableCell>{t('subscriptionPlan.actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {plans.map((plan) => (
                                <WorkspacePlanRow
                                    key={plan.id}
                                    rowKey={plan.id}
                                    plan={plan}
                                    isCurrent={plan.id === currentPlanId}
                                    isEditing={activeRowKey === plan.id}
                                    canEdit={canEdit}
                                    isSaving={savingRowKey === plan.id && updatingPlan}
                                    isDeleting={deletingPlanId === plan.id && deletingPlan}
                                    onEdit={() => setActiveRowKey(plan.id)}
                                    onDelete={() => setPlanToDelete(plan)}
                                    onCancel={() => setActiveRowKey(null)}
                                    onSave={(values) => handleUpdate(plan, values)}
                                />
                            ))}
                            {isAddingRow && (
                                <WorkspacePlanRow
                                    rowKey={NEW_ROW_KEY}
                                    isEditing
                                    canEdit={false}
                                    isSaving={savingRowKey === NEW_ROW_KEY && creatingPlan}
                                    onCancel={() => setActiveRowKey(null)}
                                    onSave={handleCreate}
                                />
                            )}
                        </TableBody>
                    </Table>
                </PlansTableContainer>
            )}

            <ConfirmationDialog
                open={!!planToDelete}
                message={t('subscriptionPlan.deleteConfirm', { name: planToDelete?.name ?? '' })}
                handleClose={() => setPlanToDelete(null)}
                handleConfirm={handleDelete}
            />
        </div>
    );
};
